/**
 * Network — UI view for the Network page (Phase 2 / JAISIU-1652).
 *
 * Card list of instances from /network/instances. Clicking a card opens a
 * detail modal showing redis_setup, conn, pool, model choices, and enabled
 * features.
 *
 * Pure renderer — no side-effects. The host page is responsible for fetching
 * the data and passing it in as props.
 */

import { html, nothing } from "lit";
import type { InstanceRecord, InstanceStatus } from "../../../../src/network/types.ts";

export type NetworkInstance = InstanceRecord & { status: InstanceStatus };

export type NetworkListProps = {
  loading: boolean;
  instances: NetworkInstance[];
  lastError: string | null;
  statusMessage: string | null;
  onRefresh: () => void;
  onSelect?: (id: string) => void;
  selectedId?: string | null;
  onCompare?: (ids: string[]) => void;
  comparingIds?: string[];
};

export function renderNetworkList(props: NetworkListProps) {
  const selected = new Set(props.comparingIds ?? []);
  return html`
    <section class="card">
      <div class="row" style="justify-content: space-between;">
        <div>
          <div class="card-title">Network — Jaisiu Instances</div>
          <div class="card-sub">
            Discover and compare other Jaisiu instances sharing this registry.
          </div>
        </div>
        <div class="row" style="gap: 8px;">
          ${
            props.comparingIds && props.comparingIds.length >= 2
              ? html`<button class="btn primary" @click=${() => props.onCompare?.(props.comparingIds ?? [])}>
                  Compare ${props.comparingIds.length} selected
                </button>`
              : nothing
          }
          <button class="btn" ?disabled=${props.loading} @click=${props.onRefresh}>
            ${props.loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>
      ${
        props.lastError
          ? html`<div class="callout danger" style="margin-top: 12px;">
            ${props.lastError}
          </div>`
          : nothing
      }
      ${
        props.statusMessage
          ? html`<div class="callout" style="margin-top: 12px;">
            ${props.statusMessage}
          </div>`
          : nothing
      }
      <div class="list" style="margin-top: 16px;">
        ${
          props.instances.length === 0
            ? html`
                <div class="muted">No instances reported yet.</div>
              `
            : props.instances.map((entry) => renderCard(entry, props, selected))
        }
      </div>
    </section>
  `;
}

function renderCard(entry: NetworkInstance, props: NetworkListProps, selected: Set<string>) {
  const statusClass = `chip-status chip-status-${entry.status}`;
  const isComparing = selected.has(entry.id);
  const isSelected = props.selectedId === entry.id;
  return html`
    <div class="list-item" @click=${() => props.onSelect?.(entry.id)}>
      <div class="list-main">
        <div class="list-title">${entry.name || entry.id}</div>
        <div class="list-sub">${entry.hostname} · ${entry.role}</div>
        <div class="chip-row">
          <span class=${statusClass}>
            <span class="chip-status-dot" aria-hidden="true"></span>${entry.status}
          </span>
          <span class="chip">${entry.version}</span>
          ${entry.profile ? html`<span class="chip">profile: ${entry.profile}</span>` : nothing}
          <span class="chip">redis: ${entry.redisSetup.type}</span>
          ${entry.models.slice(0, 2).map((m) => html`<span class="chip">${m}</span>`)}
          ${
            entry.models.length > 2
              ? html`<span class="chip">+${entry.models.length - 2} more</span>`
              : nothing
          }
        </div>
      </div>
      <div class="list-meta">
        <label class="row" style="gap: 6px;" @click=${(e: Event) => e.stopPropagation()}>
          <input
            type="checkbox"
            ?checked=${isComparing}
            @change=${(e: Event) => {
              const next = new Set(selected);
              if ((e.target as HTMLInputElement).checked) {
                next.add(entry.id);
              } else {
                next.delete(entry.id);
              }
              props.onCompare?.([...next]);
            }}
          />
          <span class="muted">compare</span>
        </label>
        <div class="muted">last seen ${formatAge(entry.lastSeen)}</div>
        ${
          isSelected
            ? html`<button
                class="btn"
                @click=${(e: Event) => {
                  e.stopPropagation();
                  props.onSelect?.("");
                }}
              >
                Close detail
              </button>`
            : html`<button
                class="btn"
                @click=${(e: Event) => {
                  e.stopPropagation();
                  props.onSelect?.(entry.id);
                }}
              >
                Details
              </button>`
        }
      </div>
    </div>
  `;
}

export type NetworkDetailProps = {
  instance: NetworkInstance | null;
  onClose: () => void;
};

export function renderNetworkDetail(props: NetworkDetailProps) {
  const inst = props.instance;
  if (!inst) {
    return html`
      <div class="card"><div class="muted">No instance selected.</div></div>
    `;
  }
  return html`
    <section class="card" data-instance-detail="${inst.id}">
      <div class="row" style="justify-content: space-between;">
        <div>
          <div class="card-title">${inst.name} <span class="muted">(${inst.id})</span></div>
          <div class="card-sub">${inst.role} · ${inst.version} · ${inst.hostname}</div>
        </div>
        <button class="btn" @click=${props.onClose}>Close</button>
      </div>
      <div style="margin-top: 12px; display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));">
        <div>
          <div class="muted">Redis setup</div>
          <div><strong>${inst.redisSetup.type}</strong></div>
          ${
            inst.redisSetup.type === "cluster"
              ? html`<div class="muted">Nodes: ${inst.redisSetup.nodes?.join(", ") ?? "(none)"}</div>`
              : nothing
          }
          ${
            inst.redisSetup.type === "sentinel"
              ? html`<div class="muted">Sentinels: ${inst.redisSetup.sentinels?.join(", ") ?? "(none)"}</div>
                  <div class="muted">Master: ${inst.redisSetup.master ?? "(unset)"}</div>`
              : nothing
          }
          ${
            inst.redisSetup.type === "standalone"
              ? html`<div class="muted">${inst.redisSetup.host ?? "?"}:${inst.redisSetup.port ?? "?"} db=${inst.redisSetup.db ?? "0"}</div>`
              : nothing
          }
          <div class="muted" style="margin-top: 6px;">conn: <code>${inst.redisConn}</code></div>
        </div>
        <div>
          <div class="muted">Pool / timeouts</div>
          <ul style="margin: 4px 0; padding-left: 16px;">
            <li>connect timeout: ${inst.redisPool.connectTimeoutMs ?? "?"}ms</li>
            <li>command timeout: ${inst.redisPool.commandTimeoutMs ?? "?"}ms</li>
            <li>max retries: ${inst.redisPool.maxRetries ?? "?"}</li>
          </ul>
        </div>
        <div>
          <div class="muted">Models (${inst.models.length})</div>
          ${
            inst.models.length === 0
              ? html`
                  <div class="muted">none reported</div>
                `
              : html`<ul style="margin: 4px 0; padding-left: 16px;">
                  ${inst.models.map((m) => html`<li><code>${m}</code></li>`)}
                </ul>`
          }
        </div>
        <div>
          <div class="muted">Enabled features (${inst.enabledFeatures.length})</div>
          ${
            inst.enabledFeatures.length === 0
              ? html`
                  <div class="muted">none reported</div>
                `
              : html`<div class="chip-row">
                  ${inst.enabledFeatures.map((f) => html`<span class="chip">${f}</span>`)}
                </div>`
          }
        </div>
      </div>
      <div style="margin-top: 12px;">
        <div class="muted">Status: ${inst.status} · last seen ${formatAge(inst.lastSeen)}</div>
        ${
          inst.profile
            ? html`<div class="muted">profile: <code>${inst.profile}</code></div>`
            : nothing
        }
        ${inst.build ? html`<div class="muted">build: <code>${inst.build}</code></div>` : nothing}
      </div>
    </section>
  `;
}

function formatAge(ts: number): string {
  const ageMs = Date.now() - ts;
  if (ageMs < 60_000) return `${Math.round(ageMs / 1000)}s ago`;
  if (ageMs < 60 * 60_000) return `${Math.round(ageMs / 60_000)}m ago`;
  if (ageMs < 24 * 60 * 60_000) return `${Math.round(ageMs / (60 * 60_000))}h ago`;
  return `${Math.round(ageMs / (24 * 60 * 60_000))}d ago`;
}
